import React, { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';

import Icon from '../../../icons/Icon';
import CommonPopper from '../../../components/common-popper/CommonPopper';
import { ICONS } from '../../../icons';
import ProjectActionsPopper from './project-actions-popper/ProjectActionsPopper';
import IsGranted from '../../../components/is-granted/IsGranted';
import { PROJECT_TYPES, USER_ROLES } from '../../../constants';
import CommonTooltip from '../../../components/common-tooltip/CommonTooltip';
import SonarCardDate from './SonarCardDate';

import scss from './ProjectCard.module.scss';

const ProjectCard = ({
  project,
  projectTypeOptions,
  onOpen,
  onShare,
  onDelete,
  onDeactivate,
  onEdit,
  onCollectData,
  disabled = false,
}) => {
  const { t } = useTranslation('projects');

  const {
    id,
    projectName,
    active,
    createdBy,
    createdOn,
    projectType,
    activeSharing,
    collectedFrom,
    collectedTill,
    total,
    rangeType,
  } = project || {};

  const projectIcon = useMemo(
    () => projectTypeOptions.find((op) => op.value === projectType)?.icon ?? ICONS.Logo,
    [projectType, projectTypeOptions],
  );

  const disableText = useMemo(() => {
    if (projectType === PROJECT_TYPES.SONAR && !collectedFrom && !collectedTill) {
      return t('collectDataToOpenTheProject');
    }

    return '';
  }, [t, projectType, collectedFrom, collectedTill]);

  const handleOpenProject = useCallback(() => {
    if (onOpen && active && !disabled && !disableText) {
      onOpen(project);
    }
  }, [project, onOpen, active, disableText, disabled]);

  const handleCollectData = useCallback(() => {
    if (onCollectData) {
      onCollectData(id);
    }
  }, [id, onCollectData]);

  const handleShareProject = useCallback(() => {
    if (onShare) {
      onShare(project);
    }
  }, [project, onShare]);

  const handleEditProject = useCallback(() => {
    if (onEdit) {
      onEdit(project);
    }
  }, [project, onEdit]);

  const handleDeleteProject = useCallback(() => {
    if (onDelete) {
      onDelete(project);
    }
  }, [project, onDelete]);

  const handleDeactivateProject = useCallback(() => {
    if (onDeactivate) {
      onDeactivate(project);
    }
  }, [project, onDeactivate]);

  return (
    <CommonTooltip title={disableText}>
      <div className={clsx(scss.wrapper, (!active || disabled) && scss.cardInactive)}>
        <div className={scss.infoSection}>
          <div
            className={clsx(scss.topInfoSection, (!active || disabled || disableText) && scss.topInfoSectionDisabled)}
            onClick={handleOpenProject}
            role='presentation'>
            <div className={scss.topInfoSectionIconWrapper}>
              <Icon icon={projectIcon} disableCommonColors className={scss.topInfoSectionIcon} />
            </div>

            <div className={scss.topInfoSectionData}>
              <span className={scss.textName}>{projectName}</span>
              <span className={scss.textCommon}>{active ? t('active') : t('inactive')}</span>
            </div>
          </div>

          <div className={scss.bottomInfoSection}>
            <div className={scss.rowInfo}>
              <span className={scss.textCommon}>{t('createdBy')}:</span>
              <span className={scss.textStressed}>{createdBy}</span>
            </div>
            <div className={scss.rowInfo}>
              <span className={scss.textCommon}>{t('createdOn')}:</span>
              <span className={scss.textStressed}>{createdOn}</span>
            </div>

            {projectType === PROJECT_TYPES.SONAR && collectedFrom && collectedTill ? (
              <SonarCardDate collectedFrom={collectedFrom} collectedTill={collectedTill} rangeType={rangeType} />
            ) : null}
          </div>
        </div>
        {projectType === PROJECT_TYPES.SONAR ? (
          <>
            <div className={scss.divider}></div>

            <div className={scss.dataSetsSection}>
              <span className={scss.dataSetsTitle}>{total}</span>
              <span className={scss.dataSetsLabel}>{t('dataSets')}</span>
            </div>
          </>
        ) : null}

        <IsGranted
          //!TEMPORARY ACCESS TO COLLECT DATA
          permissions={
            projectType === PROJECT_TYPES.SONAR
              ? [
                  USER_ROLES.GLOBAL_ADMIN,
                  USER_ROLES.COMPANY_ADMIN,
                  USER_ROLES.COMPANY_USER,
                  USER_ROLES.GLOBAL_USER,
                  USER_ROLES.ROLE_CUSTOMER,
                ]
              : [USER_ROLES.GLOBAL_ADMIN]
          }>
          <div className={scss.dotsWrapper}>
            <CommonPopper
              isShowCaret={false}
              disablePortal={false}
              placement='left-start'
              component={() => (
                <ProjectActionsPopper
                  id={id}
                  projectType={projectType}
                  projectName={projectName}
                  isActive={active}
                  isShareActive={activeSharing}
                  onShare={handleShareProject}
                  onEdit={handleEditProject}
                  onDelete={handleDeleteProject}
                  onDeactivate={handleDeactivateProject}
                  onCollectData={handleCollectData}
                />
              )}>
              <Icon icon={ICONS.TwoDots} disableCommonColors disableCommonStyles className={scss.dotsIcon} />
            </CommonPopper>
          </div>
        </IsGranted>
        {projectType === PROJECT_TYPES.SONAR && !collectedFrom && !collectedTill ? (
          <div className={clsx(scss.cardMarker, scss.cardMarker_red)}>
            <span>{t('noDataCollected')}</span>
          </div>
        ) : null}
      </div>
    </CommonTooltip>
  );
};

export default memo(ProjectCard);
